import { useEffect, useMemo } from 'react';

import { Outlet, useLocation } from 'react-router';

import { useCurrentUser, useLiveAllChartsByUser, useProfiles } from '@chartai/api-client';

import { NetworkStatusBanner } from '@/shared/components/NetworkStatusBanner';
import { TooltipProvider } from '@/shared/components/ui/Tooltip';
import { WakeLockBanner } from '@/shared/components/WakeLockBanner';
import { useWakeLock } from '@/shared/hooks/useWakeLock';
import { useProfileStore } from '@/shared/stores/useProfileStore';

import { Footer } from './Footer';
import { MobileSidebar } from './MobileSidebar';
import { Sidebar } from './Sidebar';
import { StatsHeader } from './StatsHeader';
import { TopBar } from './TopBar';

// Routes where the global StatsHeader is suppressed. Templates is a
// configuration surface; chart detail is a focused review/edit surface — the
// activity cards are noise in both.
const HIDDEN_STATS_HEADER_PREFIXES = ['/dashboard/templates', '/dashboard/charts/'];

export function shouldHideStatsHeader(pathname: string): boolean {
  return HIDDEN_STATS_HEADER_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function DashboardLayout() {
  const { user } = useCurrentUser();
  const { data: profiles } = useProfiles(user?.uid ?? '');
  const selectedProfile = useProfileStore((s) => s.selectedProfile);
  const initializeSelection = useProfileStore((s) => s.initializeSelection);
  const { isSupported: wakeLockSupported } = useWakeLock();
  const location = useLocation();

  // Real-time count of charts pending provider review — drives the sidebar
  // badge. Same filter the Needs Review page uses (status === 'Ready').
  const { data: charts } = useLiveAllChartsByUser(user?.uid ?? '');
  const needsReviewCount = useMemo(() => {
    return charts.filter((doc) => {
      const raw = typeof doc['status'] === 'string' ? doc['status'] : '';
      return raw.toLowerCase() === 'ready';
    }).length;
  }, [charts]);
  const hideStatsHeader = shouldHideStatsHeader(location.pathname);

  // Auto-select first profile if none selected, OR if the stored selection
  // points to a profile that no longer exists (e.g. localStorage left over
  // from a prior account / deleted profile). initializeSelection handles
  // both cases — auto-picking the first available when the stored id misses.
  useEffect(() => {
    if (!profiles || profiles.length === 0) {
      return;
    }
    const mapped = (profiles as (Record<string, unknown> & { id: string })[]).map((p) => ({
      id: p.id,
      displayName: typeof p['displayName'] === 'string' ? p['displayName'] : '',
    }));
    const storedIsStale =
      selectedProfile !== null && !mapped.some((p) => p.id === selectedProfile.id);
    if (!selectedProfile || storedIsStale) {
      initializeSelection(mapped);
    }
  }, [profiles, selectedProfile, initializeSelection]);
  return (
    <TooltipProvider>
      <div className="flex h-screen flex-col">
        {/* Top bar spanning full width with logo */}
        <TopBar />
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar — overflow-visible so toggle button can peek out */}
          <div className="relative hidden overflow-visible md:flex">
            <Sidebar needsReviewCount={needsReviewCount} />
          </div>
          <MobileSidebar needsReviewCount={needsReviewCount} />
          <div className="flex flex-1 flex-col overflow-hidden">
            <NetworkStatusBanner />
            <main className="flex flex-1 flex-col overflow-y-auto">
              {!hideStatsHeader && <StatsHeader />}
              {/*
                Hotjar PHI handling (CAB-495): we do NOT suppress this whole
                subtree — doing so blanked the entire screen in session replays.
                Instead, PHI-bearing elements are masked individually with
                `data-hj-masked` (patient name/DOB/contact, appointment rows,
                transcripts) and form inputs are auto-masked by Hotjar. This
                keeps the UI visible in replays while scrubbing PHI text.
                Standing rule: any new component that renders PHI as text MUST
                add `data-hj-masked` to its container.
              */}
              <div className="flex min-h-0 flex-1 flex-col p-4 lg:p-6">
                {!wakeLockSupported && (
                  <div className="mb-4">
                    <WakeLockBanner />
                  </div>
                )}
                <Outlet />
              </div>
            </main>
            <Footer />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
